class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: "0px 0px 400px 0px" }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== "attributes") continue;

      // Ignore error attribute changes
      if (mutation.attributeName === "data-error") continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === "class" && this.classList.contains("hidden")) continue;

      // `data-recommendations-performed` reports state, it is not an input, so a
      // change to it never means "reload". It used to: the cart drawer renders
      // this element, and `recommendations.performed` is always false on a Cart
      // AJAX section render, so every morph reset the flag we had just set to
      // 'true' and kicked off another load — on every add, quantity change and
      // removal. `#loadRecommendations` guards on the request URL instead.
      if (mutation.attributeName === "data-recommendations-performed") continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * The recommendations request URL whose results are currently rendered, so a
   * reload triggered by unrelated attribute churn is a no-op. Only a change to a
   * real input (`data-product-id` as the cart's first line changes, intent, etc.)
   * produces a different URL and an actual re-render.
   * @type {string | null}
   */
  #renderedUrl = null;

  connectedCallback() {
    // If the server rendered real recommendations (a product page render, or the
    // Theme Editor's preview context), record what they were rendered for now —
    // while the markup and the attributes still agree. Doing it lazily on first
    // load would risk adopting stale cards under a since-changed product id.
    if (this.dataset.recommendationsPerformed === "true" && this.dataset.productId) {
      const { productId, sectionId, intent } = this.dataset;
      this.#renderedUrl = this.#recommendationsUrl(productId, sectionId, intent);
    }

    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    this.#mutationObserver.disconnect();
    this.#intersectionObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, sectionId, intent, useManualProducts } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error("Product ID and an ID attribute are required");
    }

    // If manual products are being used, skip API fetch
    if (useManualProducts === "true") {
      return;
    }

    // Nothing about the request changed, so what's rendered is still correct.
    // Covers both the server-rendered case (recorded in connectedCallback) and
    // anything we fetched ourselves.
    const url = this.#recommendationsUrl(productId, sectionId, intent);
    if (url === this.#renderedUrl) {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = new DOMParser().parseFromString(result.data || "", "text/html");
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.childNodes.length) {
          this.dataset.recommendationsPerformed = "true";
          this.#renderedUrl = url;
          const content = Array.from(recommendations.childNodes, (node) => document.importNode(node, true));
          this.replaceChildren(...content);
        } else {
          this.#handleError(new Error("No recommendations available"));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Builds the recommendations request URL. Doubles as the identity of a render:
   * same URL means the rendered recommendations are still correct.
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {string}
   */
  #recommendationsUrl(productId, sectionId, intent) {
    return `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = this.#recommendationsUrl(productId, sectionId, intent);

    // Skip cache in design mode to always show fresh content when merchant edits settings
    if (!Shopify.designMode) {
      const cachedResponse = this.#cachedRecommendations[url];
      if (cachedResponse) {
        return { success: true, data: cachedResponse };
      }
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      // Only cache if not in design mode
      if (!Shopify.designMode) {
        this.#cachedRecommendations[url] = text;
      }
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error("Product recommendations error:", error.message);
    this.classList.add("hidden");
    this.dataset.error = "Error loading product recommendations";
  }
}

if (!customElements.get("product-recommendations")) {
  customElements.define("product-recommendations", ProductRecommendations);
}
